// CameraRig.tsx — One camera over the whole map (true galactic coordinates) with
// a hover-driven zoom hierarchy: hovering a selectable body brackets it, and
// scrolling IN while something is bracketed enters it (galaxy→system→planet).
// Scrolling in with nothing bracketed is capped by minDistance. Scrolling OUT
// past a level's extent steps back up (capped at the galaxy level). The system
// view is top-down on the ecliptic; the planet view tidal-locks the camera to a
// damped 3/4 view of the sunlit side, tracking the planet as it orbits.
import { useEffect, useRef, type ElementRef } from 'react';
import { CameraControls } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { Vector3 } from 'three';
import { useGame } from '../state/context';
import { E2E_ENABLED } from '../e2e';
import { gameClock } from '../state/gameClock';
import { shipTrack } from '../state/shipTrack';
import type { Moon, Planet, StarSystem } from '../types';
import { orbitPosition } from './orbit';
import { systemExtent } from './systemGeometry';

interface Props {
  systems: StarSystem[];
  focusedSystemId: string | null;
  focusedPlanetId: string | null;
  focusedMoonId: string | null;
  selectedSingularityId: string | null;
  /** A selected ship to chase (its drawn position comes from state/shipTrack), or null. */
  followShipId?: string | null;
}

// Chase distances when a ship is selected: close in a system, a regional view mid-jump.
const FOLLOW_DIST_SYSTEM = 2.2;
const FOLLOW_DIST_GALAXY = 28;

// Top-down over the galactic plane (tiny x offset to avoid gimbal lock).
const OVERVIEW: [number, number, number] = [2.4, 120, 0];
const PAN_KEYS = new Set(['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']);
// Mouse within this many px of a screen edge pans the open map toward it (RTS-style).
const EDGE_PAN_PX = 40;

const GALAXY_MAX_DIST = 220; // cap zoom-out at the galaxy level
const GALAXY_MIN_DIST = 8; // cap zoom-in when nothing is bracketed
const SYSTEM_MIN_DIST = 1.5; // cap zoom-in when no planet is bracketed
const SYSTEM_EXIT_FACTOR = 3.2; // dist > systemExtent*this → back to galaxy
const PLANET_EXIT_FACTOR = 4.0; // dist > planetExtent*this → back to system

// Point-of-singularity close-up. SING_R mirrors Singularity.tsx's CORE; the visible
// glow reaches ~4.5x that, so the framing is sized to the glow, not the dark core.
const SING_R = 0.05;
const SING_VIS = SING_R * 4.5;

function planetWorldPos(system: StarSystem, planet: Planet, t: number, out: Vector3): Vector3 {
  orbitPosition(planet, t, out);
  const inc = planet.inclination;
  const z = out.z;
  out.set(out.x, -z * Math.sin(inc), z * Math.cos(inc));
  out.x += system.position[0];
  out.y += system.position[1];
  out.z += system.position[2];
  return out;
}

// World position of a moon: the planet's orbital position plus the moon's own orbit
// (inclined in the planet's frame), the whole sum then inclined by the planet's plane
// and offset to the star — the same transform chain the scene graph applies
// (SystemDetail → planet inclination → planet orbit → moon inclination → moon orbit).
// Reduces to planetWorldPos when the moon offset is zero.
const _moff = new Vector3();
function moonWorldPos(system: StarSystem, planet: Planet, moon: Moon, t: number, out: Vector3): Vector3 {
  // Moon offset within the planet's pre-inclination frame: Rx(moon.inc) * orbit(moon).
  orbitPosition(moon, t, _moff);
  const mz = _moff.z;
  _moff.set(_moff.x, -mz * Math.sin(moon.inclination), mz * Math.cos(moon.inclination));
  // Planet orbital position (pre-inclination), plus the moon offset.
  orbitPosition(planet, t, out);
  out.add(_moff);
  // Apply the planet's orbital-plane inclination, then offset to the star.
  const pInc = planet.inclination;
  const sy = out.y;
  const sz = out.z;
  out.set(out.x, sy * Math.cos(pInc) - sz * Math.sin(pInc), sy * Math.sin(pInc) + sz * Math.cos(pInc));
  out.x += system.position[0];
  out.y += system.position[1];
  out.z += system.position[2];
  return out;
}

function planetExtent(planet: Planet): number {
  let max = planet.radius * 3.5;
  for (const m of planet.moons ?? []) {
    max = Math.max(max, (m.semiMajorAxis * (1 + m.eccentricity) + m.radius) * 1.4);
  }
  return max;
}

// Camera position for a 3/4 view of the sunlit side: mostly toward the star, with
// a slight elevation + sideways offset so the lit hemisphere and terminator show.
const _sunDir = new Vector3();
const _right = new Vector3();
const _up = new Vector3();
const _dir = new Vector3();
const _gdir = new Vector3();
const WORLD_UP = new Vector3(0, 1, 0);

function sunSideCamPos(planet: Vector3, sun: Vector3, dist: number, out: Vector3): Vector3 {
  _sunDir.subVectors(sun, planet).normalize();
  _right.crossVectors(_sunDir, WORLD_UP);
  if (_right.lengthSq() < 1e-6) {
    _right.set(1, 0, 0);
  }
  _right.normalize();
  _up.crossVectors(_right, _sunDir).normalize();
  _dir
    .copy(_sunDir)
    .multiplyScalar(0.88)
    .addScaledVector(_up, 0.32)
    .addScaledVector(_right, 0.22)
    .normalize();
  return out.copy(planet).addScaledVector(_dir, dist);
}

export function CameraRig({
  systems,
  focusedSystemId,
  focusedPlanetId,
  focusedMoonId,
  selectedSingularityId,
  followShipId = null,
}: Props) {
  const { focus, focusUp, hoveredId } = useGame();
  const camera = useThree((s) => s.camera);
  const viewport = useThree((s) => s.size);
  const ref = useRef<ElementRef<typeof CameraControls>>(null);
  const proj = useRef(new Vector3());
  const pressed = useRef<Set<string>>(new Set());
  // Latest cursor position in viewport px (x:-1 when the pointer has left the window).
  const mouseRef = useRef({ x: -1, y: -1 });
  const wp = useRef(new Vector3());
  const cam = useRef(new Vector3());
  const sun = useRef(new Vector3());
  const orb = useRef(new Vector3());
  const suppressing = useRef(false);
  const stopTimer = useRef<number | null>(null);
  // Starting follow distance for the fly-in; the user can dolly from here.
  const planetDist = useRef(0);
  // Previous tidal-lock azimuth (the planet's angle around its star). The planet
  // camera co-rotates by the per-frame delta so a tidally-locked world keeps the
  // same face in view as it orbits. null until the first planet-mode frame.
  const prevLock = useRef<number | null>(null);

  // Refs read by the (once-bound) wheel handler, kept fresh each render.
  const hoverRef = useRef(hoveredId);
  const sysRef = useRef(focusedSystemId);
  const planetRef = useRef(focusedPlanetId);
  const singRef = useRef(selectedSingularityId);
  const systemsRef = useRef(systems);
  const focusRef = useRef(focus);
  const focusUpRef = useRef(focusUp);
  hoverRef.current = hoveredId;
  sysRef.current = focusedSystemId;
  planetRef.current = focusedPlanetId;
  singRef.current = selectedSingularityId;
  systemsRef.current = systems;
  focusRef.current = focus;
  focusUpRef.current = focusUp;

  useEffect(() => {
    const c = ref.current;
    if (c) {
      c.dollyToCursor = true;
    }
  }, []);

  // Mode changes happen ONLY on wheel events (never per-frame on distance, which
  // would flap during the entry animation): scroll IN onto a bracketed body to
  // enter it; scroll OUT once already at this level's max distance to step up.
  // After a switch the wheel is disabled until scrolling actually STOPS (a quiet
  // gap), so a freewheeling wheel's leftover velocity can't blow through layers.
  useEffect(() => {
    const armReenable = () => {
      if (stopTimer.current) {
        window.clearTimeout(stopTimer.current);
      }
      stopTimer.current = window.setTimeout(() => {
        suppressing.current = false;
        const c = ref.current;
        if (c) {
          c.enabled = true;
        }
      }, 160);
    };
    const suppress = () => {
      suppressing.current = true;
      const c = ref.current;
      if (c) {
        // Ignore user input (the freewheel's leftover velocity) until it stops;
        // programmatic setLookAt for the layer transition still runs.
        c.enabled = false;
      }
      armReenable();
    };
    const onWheel = (e: WheelEvent) => {
      const c = ref.current;
      if (!c) {
        return;
      }
      if (suppressing.current) {
        armReenable(); // still spinning down — keep waiting
        return;
      }
      if (e.deltaY < 0) {
        const hid = hoverRef.current;
        if (!hid) {
          return;
        }
        if (sysRef.current === null) {
          if (systemsRef.current.some((s) => s.id === hid)) {
            focusRef.current(hid);
            suppress();
          }
        } else if (planetRef.current === null && singRef.current === null) {
          const sys = systemsRef.current.find((s) => s.id === sysRef.current);
          if (sys && sys.planets.some((p) => p.id === hid)) {
            focusRef.current(hid);
            suppress();
          } else if (sys && hid.startsWith(sys.id + '>')) {
            // A point-of-singularity is hovered (its id is `${sys}>${neighbor}`);
            // scroll in to zoom to it, the same gesture as entering a planet.
            focusRef.current(hid);
            suppress();
          }
        }
      } else if (e.deltaY > 0) {
        // Planet/singularity close-ups pin a near distance (no "zoomed all the way
        // out" to detect by distance), so any scroll-out steps up a level. System
        // mode still waits until we're actually at the galaxy-level max distance.
        if (planetRef.current !== null || singRef.current !== null) {
          focusUpRef.current();
          suppress();
        } else if (sysRef.current !== null && c.distance >= c.maxDistance * 0.97) {
          focusUpRef.current();
          suppress();
        }
      }
    };
    window.addEventListener('wheel', onWheel, { passive: true });
    return () => window.removeEventListener('wheel', onWheel);
  }, []);

  useEffect(() => {
    const c = ref.current;
    if (!c) {
      return;
    }
    const sys = focusedSystemId ? systems.find((s) => s.id === focusedSystemId) ?? null : null;
    // Orbiting is allowed in every mode (including planet mode — the follow is a
    // translate, so dragging to look around composes with it).
    c.azimuthRotateSpeed = 1;
    c.polarRotateSpeed = 1;
    // Cursor-anchored dolly is nice on the open map but fights the per-frame target
    // re-centre in planet mode, so keep it off there.
    c.dollyToCursor = focusedPlanetId === null;

    if (!sys) {
      c.minDistance = GALAXY_MIN_DIST;
      c.maxDistance = GALAXY_MAX_DIST;
      c.smoothTime = 0.25;
      void c.setLookAt(OVERVIEW[0], OVERVIEW[1], OVERVIEW[2], 0, 0, 0, true);
      return;
    }
    sun.current.set(sys.position[0], sys.position[1], sys.position[2]);

    // A focused moon is one level deeper than its planet (which is also derived-
    // focused), so check it first. Fly to a sun-side close-up; useFrame then follows
    // the moon as it orbits its planet (which orbits the star).
    if (focusedMoonId) {
      const planet = sys.planets.find((p) => p.id === focusedPlanetId);
      const moon = (planet?.moons ?? []).find((m) => m.id === focusedMoonId);
      if (planet && moon) {
        const ext = moon.radius * 3.5;
        c.minDistance = moon.radius * 1.3;
        c.maxDistance = ext * PLANET_EXIT_FACTOR * 1.3;
        c.smoothTime = 0.3;
        planetDist.current = ext * 1.6;
        prevLock.current = null;
        moonWorldPos(sys, planet, moon, gameClock.time, wp.current);
        sunSideCamPos(wp.current, sun.current, planetDist.current, cam.current);
        void c.setLookAt(
          cam.current.x, cam.current.y, cam.current.z,
          wp.current.x, wp.current.y, wp.current.z, true,
        );
        return;
      }
    }

    if (focusedPlanetId) {
      const planet = sys.planets.find((p) => p.id === focusedPlanetId);
      if (planet) {
        const ext = planetExtent(planet);
        c.minDistance = planet.radius * 1.3;
        c.maxDistance = ext * PLANET_EXIT_FACTOR * 1.3;
        c.smoothTime = 0.3; // damps the fly-in; useFrame then follows the orbit
        planetDist.current = ext * 1.6;
        prevLock.current = null;
        // Fly in to a 3/4 sun-side view of the planet at its current position;
        // useFrame re-centres on it every frame (setTarget) and co-rotates with a
        // tidally-locked world's frame, so it stays dead-centre without jitter.
        planetWorldPos(sys, planet, gameClock.time, wp.current);
        sunSideCamPos(wp.current, sun.current, planetDist.current, cam.current);
        void c.setLookAt(
          cam.current.x, cam.current.y, cam.current.z,
          wp.current.x, wp.current.y, wp.current.z, true,
        );
        return;
      }
    }

    // Point of singularity: a focusable entity like a planet, but static at the
    // system rim. Fly to a face-on close-up of the portal (the ring axis aims at the
    // destination), looking outward past it into deep space — the "gateway" framing.
    if (selectedSingularityId) {
      const sep = selectedSingularityId.indexOf('>');
      const neighborId = sep >= 0 ? selectedSingularityId.slice(sep + 1) : '';
      const neighbor = systems.find((s) => s.id === neighborId);
      if (neighbor) {
        const rim = systemExtent(sys);
        // Direction system→neighbor (matches Singularity.tsx's placement of the gate).
        _gdir
          .set(
            neighbor.position[0] - sys.position[0],
            neighbor.position[1] - sys.position[1],
            neighbor.position[2] - sys.position[2],
          )
          .normalize();
        wp.current
          .set(sys.position[0], sys.position[1], sys.position[2])
          .addScaledVector(_gdir, rim);
        c.minDistance = SING_R * 1.5;
        c.maxDistance = SING_VIS * 9;
        c.smoothTime = 0.3;
        // Sit on the system side of the gate looking outward (+gdir), slightly raised.
        cam.current
          .copy(wp.current)
          .addScaledVector(_gdir, -SING_VIS * 2.6)
          .addScaledVector(WORLD_UP, SING_VIS * 0.7);
        void c.setLookAt(
          cam.current.x, cam.current.y, cam.current.z,
          wp.current.x, wp.current.y, wp.current.z, true,
        );
        return;
      }
    }

    // System: top-down on the ecliptic plane (tiny offset to avoid gimbal lock).
    const ext = systemExtent(sys);
    const d = ext * 2.2;
    c.minDistance = SYSTEM_MIN_DIST;
    c.maxDistance = ext * SYSTEM_EXIT_FACTOR * 1.25;
    c.smoothTime = 0.25;
    const [sx, sy, sz] = sys.position;
    void c.setLookAt(sx + d * 0.02, sy + d, sz, sx, sy, sz, true);
  }, [focusedSystemId, focusedPlanetId, focusedMoonId, selectedSingularityId, systems]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (PAN_KEYS.has(e.key)) {
        pressed.current.add(e.key);
        e.preventDefault();
      }
    };
    const up = (e: KeyboardEvent) => {
      pressed.current.delete(e.key);
    };
    const move = (e: MouseEvent) => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
    };
    const leave = () => {
      mouseRef.current.x = -1;
      mouseRef.current.y = -1;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseout', leave);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseout', leave);
    };
  }, []);

  // Starting (or re-scoping) a ship chase: zoom to a sensible chase distance. In a system
  // that's a close-up; mid-jump (galaxy view) a regional view that shows the lane.
  useEffect(() => {
    const c = ref.current;
    if (!c || !followShipId) return;
    void c.dollyTo(focusedSystemId ? FOLLOW_DIST_SYSTEM : FOLLOW_DIST_GALAXY, true);
  }, [followShipId, focusedSystemId]);

  useFrame((_state, delta) => {
    const c = ref.current;
    if (!c) {
      return;
    }

    // Chase a selected ship: translate the pivot onto where the ship is DRAWN this frame
    // (published at priority -1 by Ship/ShipMarker), preserving the user's azimuth and
    // distance, then apply it this frame (same one-frame-lag fix as the planet follow).
    if (followShipId) {
      const p = shipTrack.get(followShipId);
      if (p) {
        void c.moveTo(p.x, p.y, p.z, false);
        c.update(0);
        return;
      }
    }

    const keys = pressed.current;
    // Pan the open map with the arrow keys and (in galaxy view) by nudging the cursor
    // to a screen edge — RTS-style. Skipped in planet/moon close-ups, where the
    // per-frame follow would just undo it (you orbit there instead).
    if (!focusedPlanetId) {
      const speed = c.distance * 0.6 * delta;
      let x = 0;
      let y = 0;
      if (keys.has('ArrowLeft')) x -= speed;
      if (keys.has('ArrowRight')) x += speed;
      // truck +y moves the view down, so up/down map to the negative/positive sense.
      if (keys.has('ArrowUp')) y -= speed;
      if (keys.has('ArrowDown')) y += speed;
      // Screen-edge pan, galaxy view only (the system/planet panels sit on the right
      // edge, so edge-panning there would fight the cursor reaching them). Disabled
      // under ?e2e=1: the playtest parks the cursor in screen corners to clear hover,
      // which would otherwise pan the camera and desync its deterministic flow.
      if (focusedSystemId === null && !E2E_ENABLED) {
        const { x: mx, y: my } = mouseRef.current;
        if (mx >= 0) {
          if (mx < EDGE_PAN_PX) x -= speed;
          else if (mx > viewport.width - EDGE_PAN_PX) x += speed;
          if (my < EDGE_PAN_PX) y -= speed;
          else if (my > viewport.height - EDGE_PAN_PX) y += speed;
        }
      }
      if (x !== 0 || y !== 0) {
        void c.truck(x, y, false);
      }
    }

    if (focusedMoonId) {
      const sys = systems.find((s) => s.id === focusedSystemId);
      const planet = sys?.planets.find((p) => p.id === focusedPlanetId);
      const moon = (planet?.moons ?? []).find((m) => m.id === focusedMoonId);
      if (!sys || !planet || !moon) {
        return;
      }
      // Follow the moon as it orbits its planet (which orbits the star): translate the
      // pivot onto the moon each frame, preserving the user's azimuth/distance.
      moonWorldPos(sys, planet, moon, gameClock.time, wp.current);
      void c.moveTo(wp.current.x, wp.current.y, wp.current.z, false);
      if (!(window as { __farstars?: { skipFollowUpdate?: boolean } }).__farstars?.skipFollowUpdate) {
        c.update(0);
      }
      return;
    }

    if (focusedPlanetId) {
      const sys = systems.find((s) => s.id === focusedSystemId);
      const planet = sys?.planets.find((p) => p.id === focusedPlanetId);
      if (!sys || !planet) {
        return;
      }
      // Follow by translating the orbit pivot (and the camera with it) onto the
      // planet: moveTo preserves the user's azimuth/polar/distance, so the planet
      // stays dead-centre, dragging to orbit still works, and the prior per-frame
      // setLookAt shimmer is gone. A tidally-locked world additionally co-rotates the
      // camera by its frame's per-frame delta so the same face stays centred as it
      // orbits (free-spinning worlds rotate under a fixed view).
      planetWorldPos(sys, planet, gameClock.time, wp.current);
      void c.moveTo(wp.current.x, wp.current.y, wp.current.z, false);
      if (planet.tidallyLocked) {
        // Match the surface's lock basis exactly: it spins by atan2(x, z) of the
        // pre-inclination orbital position (Planet.tsx), so co-rotate on that, not
        // the inclination-skewed world position.
        orbitPosition(planet, gameClock.time, orb.current);
        const lock = Math.atan2(orb.current.x, orb.current.z);
        if (prevLock.current !== null) {
          const d = Math.atan2(
            Math.sin(lock - prevLock.current),
            Math.cos(lock - prevLock.current),
          );
          if (d !== 0) {
            void c.rotate(d, 0, false);
          }
        }
        prevLock.current = lock;
      } else {
        prevLock.current = null;
      }
      // Apply the new target THIS frame. CameraControls writes the camera transform
      // only inside its own update(), which (as our child) is subscribed before this
      // follow useFrame and so otherwise runs a frame behind — the camera would
      // chase last frame's planet position, jittering back and forth as the orbit
      // moves (worst when zoomed in close). update(0) re-derives the transform from
      // the just-set target without advancing damping.
      // (Test A/B: window.__farstars.skipFollowUpdate disables this to measure the
      // unfixed one-frame lag.)
      if (!(window as { __farstars?: { skipFollowUpdate?: boolean } }).__farstars?.skipFollowUpdate) {
        c.update(0);
      }

      // Test-only, skew-free tracking probe: this is the one spot where the planet's
      // rendered world position (wp) and the just-positioned camera exist together
      // for THIS frame (both read gameClock.time before GameClock advances it), so
      // the planet's on-screen offset here is ground truth. Inert unless the
      // playtest set window.__farstars.track to an array.
      const rec = (window as { __farstars?: { track?: { x: number; y: number }[] } }).__farstars
        ?.track;
      if (rec) {
        camera.updateMatrixWorld();
        proj.current.copy(wp.current).project(camera);
        rec.push({
          x: (proj.current.x * 0.5 + 0.5) * viewport.width,
          y: (-proj.current.y * 0.5 + 0.5) * viewport.height,
        });
      }
    }
  });

  return <CameraControls ref={ref} makeDefault minDistance={GALAXY_MIN_DIST} dollySpeed={0.6} />;
}
